
import {
  ProductOrderDto,
  CreateProductOrderDto,
  CancelProductOrderDto,
  ProductOrderStatus,
  ProductOrderPaymentStatus,
  ProductOrderReceiptNewProductDto,
  CreditNoteChoiceDto,
  CreateProductOrderPaymentDto,
} from './dtos'
import { BaseCrudService } from './BaseCrudService'
import { axios, PaginatedQueryBase } from './api'
import { PaginatedDto } from './extendedType'

export type ProductOrdersFilters = PaginatedQueryBase & {
  statuses?: ProductOrderStatus[]
  paymentStatus?: ProductOrderPaymentStatus
  from?: string
  to?: string
  internalOrderNumber?: string
}

export type FindScheduledDeliveriesQuery = PaginatedQueryBase & {
  date: string
}

const endpoint = 'product-order'
const baseUrl = process.env.REACT_APP_RADIOPHARMACY_API_URL

class ProductOrderService extends BaseCrudService<
  ProductOrderDto,
  CreateProductOrderDto,
  {},
  ProductOrdersFilters
> {
  constructor() {
    super(endpoint, baseUrl)
  }

  subscribeToEvents(onProductOrderUpdated: (productOrder: ProductOrderDto) => void): () => void {
    const eventSource = new EventSource(`${baseUrl}/${endpoint}/events`)
    eventSource.addEventListener('productOrder.status.updated', (messageEvent) => {
      const productOrder: ProductOrderDto = JSON.parse((messageEvent as MessageEvent).data)
      onProductOrderUpdated(productOrder)
    })
    eventSource.onerror = () => console.error(`SSE ${endpoint} connection error`)
    return () => eventSource.close()
  }

  acknowledge(id: string, file: File, supplierOrderNumber?: string): Promise<ProductOrderDto> {
    const form = new FormData()
    form.append('file', file)
    if (supplierOrderNumber) {
      form.append('supplierOrderNumber', supplierOrderNumber)
    }

    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'post',
        `${this.endpoint}/${id}/acknowledge`,
        form
      )
      axios(configs, resolve, reject)
    })
  }

  cancel(id: string, dto: CancelProductOrderDto): Promise<ProductOrderDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'post',
        `${this.endpoint}/${id}/cancel`,
        dto
      )
      axios(configs, resolve, reject)
    })
  }

  getToValidate(filters: PaginatedQueryBase): Promise<PaginatedDto<ProductOrderDto>> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'get',
        `${this.endpoint}/to-validate`,
        undefined,
        filters as any
      )
      axios(configs, resolve, reject)
    })
  }

  findNewProductsReceived(id: string): Promise<{ newProduct: ProductOrderReceiptNewProductDto, quantity: number }[]> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'get',
        `${this.endpoint}/${id}/new-products-received`,
        undefined,
      )
      axios(configs, resolve, reject)
    })
  }

  markOrderAsReceived(id: string): Promise<ProductOrderDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'post',
        `${this.endpoint}/${id}/received`,
        undefined
      )
      axios(configs, resolve, reject)
    })
  }

  retryFailedMailSending(id: string): Promise<ProductOrderDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'post',
        `${this.endpoint}/${id}/retry-mail`,
        undefined
      )
      axios(configs, resolve, reject)
    })
  }

  validateOrder(id: string): Promise<ProductOrderDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'post',
        `${this.endpoint}/${id}/validate`,
        undefined
      )
      axios(configs, resolve, reject)
    })
  }

  declineOrder(id: string): Promise<ProductOrderDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'post',
        `${this.endpoint}/${id}/decline`,
        undefined
      )
      axios(configs, resolve, reject)
    })
  }

  saveCreditNoteChoice(id: string, dto: CreditNoteChoiceDto): Promise<ProductOrderDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'post',
        `${this.endpoint}/${id}/credit-note-choice`,
        dto
      )
      axios(configs, resolve, reject)
    })
  }

  registerPayment(id: string, dto: CreateProductOrderPaymentDto, file?: File): Promise<ProductOrderDto> {
    const form = new FormData()
    form.append('payload', JSON.stringify(dto))
    if (file) {
      form.append('file', file)
    }
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('post', `${this.endpoint}/${id}/payments`, form)
      axios(configs, resolve, reject)
    })
  }

  markOrderAsPaid(id: string): Promise<ProductOrderDto> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig('post', `${this.endpoint}/${id}/mark-as-paid`, undefined)
      axios(configs, resolve, reject)
    })
  }

  findScheduledDeliveries(filters: FindScheduledDeliveriesQuery): Promise<PaginatedDto<ProductOrderDto>> {
    return new Promise((resolve, reject) => {
      const configs = this.buildConfig(
        'get',
        `${this.endpoint}/scheduled-deliveries`,
        undefined,
        filters as any
      )
      axios(configs, resolve, reject)
    })
  }
}

export const productOrderService = new ProductOrderService()
